/**
 * The choices a design carries: its corners, and how many pages it has.
 *
 * Both questions belong to the shop's product options - they are priced there
 * and answered there - and the designer offers the same answers, with the same
 * prices, so each choice can be made where its effect can be seen. Whatever is
 * chosen here goes back to the product page with the design, which then holds
 * the form to it.
 */

import { useEffect, useRef, useState } from 'react';
import { getConfig } from '../api';
import { useEditor } from '../state/store';
import { setCorners, setPageCount } from '../state/actions';
import { formatLength } from '../model/units';
import { printedPages } from '../model/types';
import { IconChevron } from './icons';

/** The answer a document is on, or the question's default when it has none. */
function useChoice() {
  const question = getConfig().corners;
  const corners = useEditor( ( s ) => s.doc?.corners );

  if ( ! question || question.answers.length === 0 ) return null;

  const chosen =
    question.answers.find( ( a ) => a.key === corners?.choice ) ??
    question.answers.find( ( a ) => a.key === question.fallback ) ??
    question.answers[ 0 ]!;

  return { question, chosen };
}

/** In the header, beside what else was chosen: a chip that opens the answers. */
export function CornersChip() {
  const found = useChoice();
  const unit = useEditor( ( s ) => s.unit );
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLSpanElement>( null );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  if ( ! found ) return null;

  const { question, chosen } = found;

  return (
    <span className="tb-corners" ref={ wrap }>
      <button
        type="button"
        className={ `tb-bar__chip tb-corners__chip${ chosen.radius > 0 ? ' is-round' : '' }` }
        aria-haspopup="menu"
        aria-expanded={ open }
        title={ question.label }
        onClick={ () => setOpen( ! open ) }
      >
        <CornerMark round={ chosen.radius > 0 } />
        { chosen.label }
        <IconChevron size={ 12 } />
      </button>

      { open && (
        <div className="tb-menu tb-corners__menu" role="menu" aria-label={ question.label }>
          { question.answers.map( ( answer ) => (
            <button
              key={ answer.key }
              type="button"
              role="menuitemradio"
              aria-checked={ answer.key === chosen.key }
              className="tb-menu__item"
              onClick={ () => {
                setCorners( answer );
                setOpen( false );
              } }
            >
              <span className="tb-menu__tick" aria-hidden="true">{ answer.key === chosen.key ? '✓' : '' }</span>
              <CornerMark round={ answer.radius > 0 } />
              <span className="tb-menu__label">
                { answer.label }
                { answer.radius > 0 && <span className="tb-corners__radius"> · { formatLength( answer.radius, unit ) }</span> }
              </span>
              { answer.price && <span className="tb-menu__keys">{ answer.price }</span> }
            </button>
          ) ) }
        </div>
      ) }
    </span>
  );
}

/** In the page settings: the same answers, laid out as a choice. */
export function CornersField() {
  const found = useChoice();
  const unit = useEditor( ( s ) => s.unit );

  if ( ! found ) return null;

  const { question, chosen } = found;

  return (
    <div className="tb-field tb-corners__field">
      <span className="tb-field__label">{ question.label }</span>
      <div className="tb-corners__options" role="radiogroup" aria-label={ question.label }>
        { question.answers.map( ( answer ) => (
          <button
            key={ answer.key }
            type="button"
            role="radio"
            aria-checked={ answer.key === chosen.key }
            className={ `tb-corners__option${ answer.key === chosen.key ? ' is-on' : '' }` }
            onClick={ () => setCorners( answer ) }
          >
            <CornerMark round={ answer.radius > 0 } />
            <span className="tb-corners__words">
              <strong>{ answer.label }</strong>
              <span>
                { answer.radius > 0 ? formatLength( answer.radius, unit ) : 'Cut straight' }
                { answer.price ? ` · ${ answer.price }` : '' }
              </span>
            </span>
          </button>
        ) ) }
      </div>
    </div>
  );
}

function CornerMark( { round }: { round: boolean } ) {
  return (
    <svg className="tb-corners__mark" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      { round ? <path d="M3 14V8a5 5 0 0 1 5-5h6" /> : <path d="M3 14V3h11" /> }
    </svg>
  );
}

/* ---------------------------------------------------------------
 * How many pages
 * ------------------------------------------------------------ */

/** The page count a document is on, and what else the product offers. */
function usePages() {
  const question = getConfig().booklet?.question;
  const count = useEditor( ( s ) => ( s.doc ? printedPages( s.doc ) : 0 ) );
  const booklet = useEditor( ( s ) => !! s.doc?.booklet );

  if ( ! booklet || ! question || question.answers.length === 0 ) return null;

  return { question, count };
}

/**
 * In the header: how many pages the catalogue has, and the other lengths the
 * shop sells it in, with their prices.
 */
export function PagesChip( { onProblem }: { onProblem?: ( said: string ) => void } ) {
  const found = usePages();
  const [ open, setOpen ] = useState( false );
  const wrap = useRef<HTMLSpanElement>( null );

  useEffect( () => {
    if ( ! open ) return;

    const away = ( event: MouseEvent ) => {
      if ( wrap.current && ! wrap.current.contains( event.target as Node ) ) setOpen( false );
    };
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      event.preventDefault();
      setOpen( false );
    };

    window.addEventListener( 'mousedown', away );
    window.addEventListener( 'keydown', key, true );

    return () => {
      window.removeEventListener( 'mousedown', away );
      window.removeEventListener( 'keydown', key, true );
    };
  }, [ open ] );

  if ( ! found ) return null;

  const { question, count } = found;

  return (
    <span className="tb-corners" ref={ wrap }>
      <button
        type="button"
        className="tb-bar__chip tb-corners__chip"
        aria-haspopup="menu"
        aria-expanded={ open }
        title={ question.label }
        onClick={ () => setOpen( ! open ) }
      >
        <PagesMark />
        { count } pages
        <IconChevron size={ 12 } />
      </button>

      { open && (
        <div className="tb-menu tb-corners__menu" role="menu" aria-label={ question.label }>
          { question.answers.map( ( answer ) => (
            <button
              key={ answer.key }
              type="button"
              role="menuitemradio"
              aria-checked={ answer.count === count }
              className="tb-menu__item"
              onClick={ () => {
                const trouble = setPageCount( answer );

                if ( trouble ) onProblem?.( trouble );

                setOpen( false );
              } }
            >
              <span className="tb-menu__tick" aria-hidden="true">{ answer.count === count ? '✓' : '' }</span>
              <span className="tb-menu__label">{ answer.label }</span>
              { answer.price && <span className="tb-menu__keys">{ answer.price }</span> }
            </button>
          ) ) }
        </div>
      ) }
    </span>
  );
}

function PagesMark() {
  return (
    <svg className="tb-corners__mark" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M8 3.5c-1.6-1-3.4-1-5 0v9c1.6-1 3.4-1 5 0m0-9c1.6-1 3.4-1 5 0v9c-1.6-1-3.4-1-5 0m0-9v9" />
    </svg>
  );
}
